import type { FormFieldDef } from './parts';

// ============================================================================
// Interaction settings — what a bot owner configures under the dashboard's
// Interactions tab. Deliberately zod-free: the dashboard and the widget
// panel both import the limits and defaults here as VALUES, and neither
// bundle wants zod's runtime along for the ride (see parts.ts's header for
// the same constraint). The server validates incoming settings against
// these same limits in server/src/bots/interactions.ts.
//
// What each piece is, in the terms other chat products use:
//   starters    — "suggested questions" / "conversation starters": buttons
//                 under the greeting that send a message with one click.
//   leadCapture — "pre-chat form" / "lead form": collect the visitor's
//                 contact details, either before the chat opens up or when
//                 the assistant judges it relevant (a demo request, a
//                 question it can't answer, ...).
//   links       — "CTA buttons" / "link cards": owner-approved links the
//                 assistant may offer as buttons (pricing page, booking,
//                 docs). The assistant picks by id — it can never invent a
//                 URL, exactly like show_image and its allowlist.
// ============================================================================

export const MAX_STARTERS = 6;
export const MAX_STARTER_LABEL_CHARS = 60;
export const MAX_STARTER_MESSAGE_CHARS = 300;

export const MAX_LEAD_FIELDS = 6;
export const MAX_LEAD_TITLE_CHARS = 100;
export const MAX_LEAD_INTRO_CHARS = 200;

export const MAX_LINKS = 8;
export const MAX_LINK_LABEL_CHARS = 40;
export const MAX_LINK_DESCRIPTION_CHARS = 120;
export const MAX_LINK_URL_CHARS = 2000;

export const MAX_FIELD_LABEL_CHARS = 60;
export const MAX_FIELD_OPTIONS = 10;

export interface ConversationStarter {
  id: string;
  /** What the button says. */
  label: string;
  /** What is actually sent as the visitor's message. Empty means "send
   * the label itself". */
  message: string;
  /** When set, the button opens this page in a new tab (a booking page,
   * say) instead of sending a message. https:// only, checked on save. */
  url?: string;
}

// after_first_message: a slim bar above the message box, shown once the
// visitor has sent something, asking for the form's fields ("Your email,
// so we can follow up"). It never blocks the chat — the visitor can keep
// typing and leave it empty.
export const LEAD_CAPTURE_MODES = ['off', 'before_chat', 'after_first_message', 'when_relevant'] as const;
export type LeadCaptureMode = (typeof LEAD_CAPTURE_MODES)[number];

/** The field kinds an owner can put on a lead form — a subset of
 * parts.ts's FORM_FIELD_TYPES, leaving out the ones that only make sense
 * when a model is composing a form on the fly (number, date, radio). */
export const LEAD_FIELD_TYPES = ['text', 'email', 'tel', 'textarea', 'select', 'checkbox'] as const;
export type LeadFieldType = (typeof LEAD_FIELD_TYPES)[number];

export const LEAD_FIELD_TYPE_LABELS: Record<LeadFieldType, string> = {
  text: 'Short text',
  email: 'Email address',
  tel: 'Phone number',
  textarea: 'Long text',
  select: 'Dropdown',
  checkbox: 'Checkbox',
};

export interface LeadCaptureConfig {
  mode: LeadCaptureMode;
  /** Heading on the form, e.g. "Before we start". */
  title: string;
  /** A sentence under the heading, e.g. "Leave your details and we'll
   * follow up if the assistant can't help." */
  intro: string;
  fields: FormFieldDef[];
}

export interface ActionLink {
  id: string;
  label: string;
  url: string;
  /** Tells the assistant when this link is the right thing to offer —
   * never shown to visitors. */
  description: string;
}

/** The parts of parts.ts's tool list an owner switches on and off
 * themselves. fileUpload and image stay out of this: the first depends on
 * a storage binding the deployment may not have, the second on a
 * server-side allowlist that isn't per-bot. */
export const OWNER_TOGGLEABLE_TOOLS = ['quickReplies', 'form'] as const;
export type OwnerToggleableTool = (typeof OWNER_TOGGLEABLE_TOOLS)[number];

export interface InteractionConfig {
  starters: ConversationStarter[];
  leadCapture: LeadCaptureConfig;
  links: ActionLink[];
  /** The chat invitation: an animated card beside the chat bubble inviting
   * visitors to ask a question, until they open the chat or close it. On
   * for sales demos, off unless the owner turns it on for everyone else.
   * Absent (settings saved before it existed) means off. */
  callout?: boolean;
}

export const DEFAULT_LEAD_FIELDS: FormFieldDef[] = [
  { name: 'name', label: 'Your name', fieldType: 'text', required: true },
  { name: 'email', label: 'Email address', fieldType: 'email', required: true },
];

export const DEFAULT_LEAD_CAPTURE: LeadCaptureConfig = {
  mode: 'off',
  title: 'Before we start',
  intro: 'Leave your details so we can follow up if the assistant can’t help.',
  fields: DEFAULT_LEAD_FIELDS,
};

export const DEFAULT_INTERACTIONS: InteractionConfig = {
  starters: [],
  leadCapture: DEFAULT_LEAD_CAPTURE,
  links: [],
  callout: false,
};

/** A stable key for a field, derived from its label — "Your name" →
 * "your_name". Both the dashboard editor and the server's normalizer use
 * this so the key a visitor's answer is stored under is predictable. */
export function fieldNameFromLabel(label: string, fallback = 'field'): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return slug || fallback;
}
